import moment from 'moment';
import { getFiscalQuarterEndDate } from 'utils/time';
import {
  getTimelineDisplayDates,
  parseIsoDateOnlyParts,
  type TimelineDisplayDates
} from 'utils/timelineDisplayDates';
import type { MonthDescriptor, MonthlyTimelineItem } from './types';

export type MonthlyDateGranularity = 'day' | 'month' | 'quarter';

export interface MonthlyResolvedDayPosition {
  startMonthKey: string;
  startDay: number;
  endMonthKey: string;
  endDay: number;
  dateGranularity: MonthlyDateGranularity;
  chronologyMs: number;
}

function getDisplayDateGranularity(
  displayDates: TimelineDisplayDates
): MonthlyDateGranularity {
  if (displayDates.start) return 'day';
  if (displayDates.month) return 'month';
  if (displayDates.quarter) return 'quarter';
  return 'day';
}

function toChronologyMs(year: number, month: number, day: number): number {
  return moment({ year, month: month - 1, day }).valueOf();
}

function momentFromIsoDateOnly(isoDate: string): moment.Moment | null {
  const parts = parseIsoDateOnlyParts(isoDate);
  if (!parts) return null;

  const parsed = moment({
    year: parts.year,
    month: parts.month - 1,
    day: parts.day
  });

  return parsed.isValid() ? parsed : null;
}

function parseMonthToken(
  monthToken: string | undefined,
  fallbackYear: number
): { month: number; year: number } | null {
  if (!monthToken) return null;
  const parsed = moment(monthToken, ['MMM YYYY', 'MMMM YYYY', 'MMM'], true);
  if (!parsed.isValid()) return null;

  return {
    month: parsed.month() + 1,
    year: monthToken.includes(' ') ? parsed.year() : fallbackYear
  };
}

function momentFromMonthEnd(parsedMonth: {
  month: number;
  year: number;
}): moment.Moment {
  const monthIndex = parsedMonth.month - 1;
  return moment({
    year: parsedMonth.year,
    month: monthIndex,
    day: moment({ year: parsedMonth.year, month: monthIndex }).daysInMonth()
  });
}

export function resolveItemDayPosition(
  item: MonthlyTimelineItem,
  months: MonthDescriptor[]
): MonthlyResolvedDayPosition | null {
  if (!months.length) return null;

  const displayDates = getTimelineDisplayDates({
    dates: item.dates,
    projectType: item.projectType || item.type || '',
    showOnTimeline: Boolean(item.showOnTimeline),
    pco: item.pco
  });

  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  const fallbackMonth = months[Math.max(0, months.length - 1)];

  let startMoment: moment.Moment | null = null;
  let endMoment: moment.Moment | null = null;

  if (displayDates.start) {
    startMoment = momentFromIsoDateOnly(displayDates.start);
  } else {
    const parsedMonth = parseMonthToken(displayDates.month, fallbackMonth.year);
    if (parsedMonth) {
      startMoment = momentFromMonthEnd(parsedMonth);
    } else if (displayDates.quarter) {
      const fiscalQuarterEnd = getFiscalQuarterEndDate(displayDates.quarter);
      if (fiscalQuarterEnd) {
        startMoment = moment(fiscalQuarterEnd);
      }
    }
  }

  if (!startMoment?.isValid()) {
    if (displayDates.end) {
      startMoment = momentFromIsoDateOnly(displayDates.end);
    }
  }

  if (!startMoment || !startMoment.isValid()) {
    return null;
  }

  if (displayDates.end) {
    endMoment = momentFromIsoDateOnly(displayDates.end);
  } else if (displayDates.endMonth) {
    const parsedEndMonth = parseMonthToken(
      displayDates.endMonth,
      startMoment.year()
    );
    if (parsedEndMonth) {
      endMoment = momentFromMonthEnd(parsedEndMonth);
    }
  }

  if (!endMoment?.isValid() || endMoment.isBefore(startMoment)) {
    endMoment = startMoment.clone();
  }

  if (
    endMoment.isBefore(
      moment({ year: firstMonth.year, month: firstMonth.month - 1 })
    )
  ) {
    return null;
  }

  if (
    startMoment.isAfter(
      moment({ year: lastMonth.year, month: lastMonth.month - 1 }).endOf(
        'month'
      )
    )
  ) {
    return null;
  }

  if (
    startMoment.isBefore(
      moment({ year: firstMonth.year, month: firstMonth.month - 1 })
    )
  ) {
    startMoment = moment({
      year: firstMonth.year,
      month: firstMonth.month - 1,
      day: 1
    });
  }

  if (
    endMoment.isAfter(
      moment({ year: lastMonth.year, month: lastMonth.month - 1 }).endOf(
        'month'
      )
    )
  ) {
    endMoment = moment({
      year: lastMonth.year,
      month: lastMonth.month - 1
    }).endOf('month');
  }

  const startMonthKey = `${startMoment.year()}-${String(
    startMoment.month() + 1
  ).padStart(2, '0')}`;
  const endMonthKey = `${endMoment.year()}-${String(
    endMoment.month() + 1
  ).padStart(2, '0')}`;

  const startYear = startMoment.year();
  const startMonth = startMoment.month() + 1;
  const startDay = startMoment.date();

  return {
    startMonthKey,
    startDay,
    endMonthKey,
    endDay: endMoment.date(),
    dateGranularity: getDisplayDateGranularity(displayDates),
    chronologyMs: toChronologyMs(startYear, startMonth, startDay)
  };
}
